import Pagination from '../../Pagination';
import SearchBar from '../../SearchBar';
import { Button } from '../../../empreenderUI/ui/Button';
import { DropdownMenu } from '../../../empreenderUI/ui/DropDown/DropdownMenu';
import { DropDownItem, DropdownMenuContent } from '../../../empreenderUI/ui/DropDown/DropdownMenuContent';
import { DropdownMenuTrigger } from '../../../empreenderUI/ui/DropDown/DropdownMenuTrigger';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../empreenderUI/ui/Tooltip/Tooltip';
import { ChevronDown, Ellipsis, Info } from 'lucide-react';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { translations } from '../../../utils/language';
import TableBodyFade from './TableBodyFade';

const EXPAND_MS = 280;
const STICKY_HEADER_OFFSET = 40; // sticky thead ~ h-10

function scrollRowIntoTableView(container: HTMLElement | null, rowKey: string | number) {
  if (!container) return;
  const el = container.querySelector(`[data-row-key="${CSS.escape(String(rowKey))}"]`) as HTMLElement | null;
  if (!el) return;
  const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - STICKY_HEADER_OFFSET;
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

function ExpandableRowPanel({ open, colSpan, render }: { open: boolean; colSpan: number; render: () => ReactNode }) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }

    setVisible(false);
    const timeout = window.setTimeout(() => setMounted(false), EXPAND_MS);
    return () => window.clearTimeout(timeout);
  }, [open]);

  if (!mounted) return null;

  return (
    <tr className='bg-gray-50/60'>
      <td colSpan={colSpan} className='p-0 border-b border-gray-200'>
        <div
          className='grid transition-[grid-template-rows] ease-out'
          style={{
            gridTemplateRows: visible ? '1fr' : '0fr',
            transitionDuration: `${EXPAND_MS}ms`,
          }}
        >
          <div className='min-h-0 overflow-hidden'>
            <div
              className='w-full max-w-full min-w-0 overflow-x-auto thin-scrollbar transition-opacity ease-out'
              style={{
                opacity: visible ? 1 : 0,
                transitionDuration: `${EXPAND_MS}ms`,
              }}
            >
              {render()}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

type Column = { label: ReactNode; className?: string; tooltip?: string };
type Action = { label: string; function: (row: any) => void };
type ActionTable = { label: ReactNode; onClick: () => void; variant?: any; visible?: boolean };

interface TableCustomProps {
  title?: ReactNode;
  description?: ReactNode;
  setSearch?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchSubmit?: () => void;
  columns: Column[];
  data?: any[];
  rows?: ReactNode[][];
  pagination?: any;
  actions?: Action[];
  filters?: ReactNode;
  actionTable?: ActionTable[];
  isLoading?: boolean;
  getData?: (page: number) => void;
  /** When provided, rows become expandable. Return content for the expanded area. */
  renderExpandedRow?: (rowData: any, index: number) => ReactNode;
  /** Controlled expanded keys (row indices or custom ids via getRowKey). */
  expandedKeys?: Array<string | number>;
  onExpandedChange?: (keys: Array<string | number>) => void;
  getRowKey?: (rowData: any, index: number) => string | number;
  hideSearch?: boolean;
  className?: string;
}

function SkeletonRows({
  count,
  columns,
  actions,
  renderExpandedRow,
}: {
  count: number;
  columns: Column[];
  actions: Action[];
  renderExpandedRow?: TableCustomProps['renderExpandedRow'];
}) {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <tr key={`skeleton-${index}`} className='border-b border-gray-200 h-13'>
          {renderExpandedRow && (
            <td className='p-2'>
              <div className='animate-pulse bg-gray-200 h-4 w-4 rounded' />
            </td>
          )}
          {columns?.map((_, colIndex) => (
            <td key={colIndex} className='p-2'>
              <div className='animate-pulse bg-gray-200 h-4 rounded' />
            </td>
          ))}
          {actions?.length > 0 && (
            <td className='w-10 p-2'>
              <div className='animate-pulse bg-gray-200 h-8 w-8 rounded' />
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

export default function TableCustom({
  title,
  description,
  setSearch,
  searchValue,
  searchPlaceholder,
  onSearchSubmit,
  columns,
  data = [],
  rows,
  pagination,
  actions = [],
  filters,
  actionTable = [],
  isLoading = false,
  getData,
  renderExpandedRow,
  expandedKeys: controlledExpanded,
  onExpandedChange,
  getRowKey = (_row, index) => index,
  hideSearch = false,
  className = '',
}: TableCustomProps) {
  const language = useSelector((state: any) => state.reducerLanguage);
  const lang = language?.data || 'pt';
  const searchPlaceholderText = searchPlaceholder ?? translations['Buscar'][lang];
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({});
  const [internalExpanded, setInternalExpanded] = useState<Array<string | number>>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const expandedKeys = controlledExpanded ?? internalExpanded;
  const setExpandedKeys = (keys: Array<string | number>) => {
    if (onExpandedChange) onExpandedChange(keys);
    else setInternalExpanded(keys);
  };

  const handleOnOpen = (id: string, state: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: state }));
  };

  const toggleExpand = (key: string | number) => {
    if (expandedKeys.includes(key)) {
      setExpandedKeys(expandedKeys.filter((k) => k !== key));
    } else {
      setExpandedKeys([...expandedKeys, key]);
    }
  };

  useEffect(() => {
    if (!renderExpandedRow || expandedKeys.length === 0) return;
    scrollRowIntoTableView(scrollRef.current, expandedKeys[expandedKeys.length - 1]);
  }, [expandedKeys, renderExpandedRow]);

  const colSpan = columns.length + (actions?.length > 0 ? 1 : 0) + (renderExpandedRow ? 1 : 0);

  return (
    <div className={`border border-gray-200 rounded-lg pb-6 relative bg-white w-full max-w-full min-w-0 overflow-hidden ${className}`}>
      <div className='flex px-4 sm:px-6 pt-6 flex-col gap-3 items-center justify-center min-w-0'>
        {(title || description) && (
          <div className='flex flex-col w-full gap-1.5 min-w-0'>
            {title && <h2 className='text-base text-gray-900 w-full font-semibold'>{title}</h2>}
            {description && <span className='text-sm text-gray-500'>{description}</span>}
          </div>
        )}
        <div className='flex w-full gap-3 flex-wrap items-center min-w-0'>
          {!hideSearch && setSearch && <SearchBar className='w-full min-w-0 sm:flex-1 sm:max-w-[400px]' placeholder={searchPlaceholderText} setSearch={setSearch} value={searchValue} onSubmit={onSearchSubmit} />}
          {filters}
          <div className='ml-auto shrink-0'>
            <div className='flex gap-2'>
              {actionTable?.map((action, i) => (
                <Button key={i} variant={action.variant || 'outline'} className={`border-gray-200 flex-1 ${action.visible === false ? 'opacity-0 pointer-events-none' : ''}`} onClick={action.onClick}>
                  {action.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div ref={scrollRef} className='mt-8 px-2 sm:px-4 w-full max-w-full min-w-0 overflow-x-auto overflow-y-auto thin-scrollbar' style={{ maxHeight: 'calc(100vh - 280px)' }}>
        <table className='w-full min-w-0 table-fixed'>
          <colgroup>
            {renderExpandedRow && <col className='w-10' />}
            {columns?.map((column, index) => (
              <col key={index} className={column.className} />
            ))}
            {actions?.length > 0 && <col className='w-10' />}
          </colgroup>
          <thead className='sticky top-0 bg-white z-10'>
            <tr className='text-left text-gray-500 font-medium text-sm border-b border-gray-200 h-10'>
              {renderExpandedRow && <th className='px-2 w-10' />}
              {columns?.map((column, index) => (
                <th className={`px-2 ${column.className || ''}`} key={index}>
                  {column.tooltip ? (
                    <span className='flex items-center gap-1'>
                      {column.label}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className='w-3 h-3' />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{column.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
              {actions?.length > 0 && <th className='w-10' />}
            </tr>
          </thead>
          <TableBodyFade
            isLoading={isLoading}
            rows={rows}
            data={data}
            skeleton={<SkeletonRows count={Math.min(12, Math.max(Number(pagination?.per_page) || 10, 8))} columns={columns} actions={actions} renderExpandedRow={renderExpandedRow} />}
            empty={
              <tr>
                <td colSpan={colSpan} className='p-8 text-center'>
                  <div className='flex flex-col items-center justify-center text-gray-500'>
                    <h3 className='text-sm font-medium text-gray-900 mb-1'>{translations['table-empty-title'][lang]}</h3>
                    <p className='text-sm text-gray-500'>{translations['table-empty-desc'][lang]}</p>
                  </div>
                </td>
              </tr>
            }
          >
            {(cachedRows, cachedData) =>
              cachedRows.map((row, index) => {
                const rowData = cachedData[index];
                const rowKey = getRowKey(rowData, index);
                const isExpanded = expandedKeys.includes(rowKey);

                return (
                  <Fragment key={rowKey}>
                    <tr data-row-key={rowKey} className='border-b border-gray-200 h-13 scroll-mt-10'>
                      {renderExpandedRow && (
                        <td className='p-2'>
                          <button type='button' onClick={() => toggleExpand(rowKey)} className='inline-flex items-center justify-center size-8 rounded-lg hover:bg-gray-100 text-gray-400' aria-expanded={isExpanded} aria-label={isExpanded ? translations['table-collapse'][lang] : translations['table-expand'][lang]}>
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        </td>
                      )}
                      {row.map((item: any, cellIndex) => (
                        <td className='p-2 text-sm max-w-0 overflow-hidden' key={cellIndex}>
                          {item}
                        </td>
                      ))}
                      {actions?.length > 0 && (
                        <td className='w-10 p-2 text-sm'>
                          <DropdownMenu isOpen={openDropdowns[String(rowKey)] || false} setIsOpen={(state) => handleOnOpen(String(rowKey), state)}>
                            <DropdownMenuTrigger>
                              <Button variant='secondary' size='icon'>
                                <Ellipsis className='h-5 w-5' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className='min-w-28' sideOffset={10}>
                              {actions?.map((action, actionIndex) => (
                                <DropDownItem key={actionIndex}>
                                  <Button
                                    variant='secondary'
                                    onClick={() => {
                                      handleOnOpen(String(rowKey), false);
                                      action.function(rowData);
                                    }}
                                    className='text-gray-900 text-center h-9 my-1 justify-center w-full'
                                  >
                                    {action.label}
                                  </Button>
                                </DropDownItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      )}
                    </tr>
                    {renderExpandedRow && <ExpandableRowPanel open={isExpanded} colSpan={colSpan} render={() => renderExpandedRow(rowData, index)} />}
                  </Fragment>
                );
              })
            }
          </TableBodyFade>
        </table>
      </div>
      {pagination && getData && <Pagination pagination={pagination} getData={getData} />}
    </div>
  );
}
